// Agent runs grid (GET /api/v1/ai/track-record/overview *_agent_invocation lists) — shared by the Overview and Add Records tabs.
import { useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useCenteredDialogStyles } from './centeredDialogStyles';

export type InProgressStatus = 'Completed' | 'In Progress' | 'Error' | 'No results';

export type InProgressItem = {
  label: string;
  flow: string;
  createdBy: string;
  status: InProgressStatus;
  startedDate: string;
  processedDate: string;
  errorMessage?: string;
  raw?: any;
};

type AgentRunsTabKey = 'in_progress' | 'error' | 'success' | 'all';

const FLOW_LABELS: Record<string, string> = {
  ENTITY_LOOKUP: 'Entity lookup',
  PROPERTY_LOOKUP: 'Property lookup',
  UPLOAD: 'Upload',
  TEMPLATE_UPLOAD: 'Upload',
  MANUAL_ADD: 'Manual add',
};

const formatFlowLabel = (flow?: string) => {
  if (!flow) return '—';
  if (FLOW_LABELS[flow]) return FLOW_LABELS[flow];
  // Fallback for a flow value not yet in FLOW_LABELS — humanize it instead of
  // showing the raw UPPER_SNAKE_CASE the backend sends.
  return flow.toLowerCase().replace(/_/g, ' ').replace(/^./, (firstChar) => firstChar.toUpperCase());
};

const formatOverviewDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Maps a raw agent invocation from the overview endpoint onto this grid.
export const mapOverviewItem = (item: any): InProgressItem => {
  const rawStatus = (item.status ?? '').toString().toUpperCase();
  const status: InProgressStatus =
    rawStatus === 'SUCCESS' || rawStatus === 'COMPLETE' || rawStatus === 'COMPLETED'
      ? 'Completed'
      : rawStatus === 'FAILED' || rawStatus === 'ERROR' || rawStatus === 'PARSE_FAILED' || rawStatus === 'AGENT_ERROR'
      ? 'Error'
      // NO_RESULTS is terminal (completed-but-empty), not still running.
      : rawStatus === 'NO_RESULTS'
      ? 'No results'
      : 'In Progress';
  return {
    label: item.label || formatFlowLabel(item.flow),
    flow: formatFlowLabel(item.flow),
    createdBy: item.created_by ?? '—',
    status,
    startedDate: formatOverviewDate(item.creation_date),
    processedDate: formatOverviewDate(item.last_update_date ?? item.creation_date),
    errorMessage: item.error_message ?? undefined,
    raw: item,
  };
};

const STATUS_STYLE_KEYS: Record<InProgressStatus, { pill: string; dot: string }> = {
  Completed: { pill: 'statusVerified', dot: 'statusVerifiedDot' },
  'In Progress': { pill: 'statusInProgress', dot: 'statusInProgressDot' },
  Error: { pill: 'statusNeedsInput', dot: 'statusNeedsInputDot' },
  'No results': { pill: 'statusNeutral', dot: 'statusNeutralDot' },
};

const DetailRow = ({ label, value }: { label: string; value: any }) => (
  <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0' }}>
    <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{label}</Typography>
    <Typography variant="body2" style={{ fontWeight: 600, textAlign: 'right' }}>{value ?? '—'}</Typography>
  </Box>
);

export const InProgressTable = ({
  classes,
  items,
  dateHeader = 'Processed Date',
  useStartedDate = false,
}: {
  classes: Record<string, string>;
  items: InProgressItem[];
  dateHeader?: string;
  useStartedDate?: boolean;
}) => {
  const [detailsItem, setDetailsItem] = useState<InProgressItem | null>(null);
  const dialogClasses = useCenteredDialogStyles();

  return (
    <Box className={classes.table}>
      <Box className={`${classes.inProgressGridRow} ${classes.tableHeaderRow}`}>
        <Typography className={classes.tableHeaderCell}>Property / entity / file</Typography>
        <Typography className={classes.tableHeaderCell}>Flow</Typography>
        <Typography className={classes.tableHeaderCell}>Created By</Typography>
        <Typography className={classes.tableHeaderCell}>Status</Typography>
        <Typography className={classes.tableHeaderCell}>{dateHeader}</Typography>
        <span />
      </Box>
      {items.map((item, index) => {
        const statusStyle = STATUS_STYLE_KEYS[item.status];
        const isError = item.status === 'Error';
        return (
          <Box
            key={`${item.raw?.bedrock_agent_invocation_id ?? item.label}-${index}`}
            className={`${classes.inProgressGridRow} ${isError ? classes.inProgressGridRowError : ''}`}
          >
            <Box style={{ minWidth: 0 }}>
              <Typography className={classes.tableCellPrimary} noWrap>{item.label}</Typography>
              {isError && item.errorMessage && (
                <Typography className={classes.agentRunsErrorText} title={item.errorMessage} noWrap>
                  {item.errorMessage}
                </Typography>
              )}
            </Box>
            <Typography className={classes.tableCell} noWrap>{item.flow}</Typography>
            <span className={classes.roleChip} title={item.createdBy}>{item.createdBy}</span>
            <span className={`${classes.statusPill} ${classes[statusStyle.pill]}`}>
              <span className={`${classes.statusDot} ${classes[statusStyle.dot]}`} />
              {item.status}
            </span>
            <Typography className={classes.tableCell} noWrap>
              {useStartedDate ? item.startedDate : item.processedDate}
            </Typography>
            <VisibilityOutlinedIcon
              className={classes.viewIconButton}
              fontSize="small"
              onClick={() => setDetailsItem(item)}
            />
          </Box>
        );
      })}

      <Dialog open={detailsItem != null} onClose={() => setDetailsItem(null)} maxWidth="xs" fullWidth classes={dialogClasses}>
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{detailsItem?.label}</Typography>
          <IconButton size="small" onClick={() => setDetailsItem(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 24 }}>
          <DetailRow label="Flow" value={detailsItem?.flow} />
          <DetailRow label="Status" value={detailsItem?.status} />
          <DetailRow label="Created By" value={detailsItem?.createdBy} />
          <DetailRow label="Created" value={detailsItem?.startedDate} />
          <DetailRow label="Last Updated" value={detailsItem?.processedDate} />
          <DetailRow label="Request ID" value={detailsItem?.raw?.request_id} />
          <DetailRow label="Invocation ID" value={detailsItem?.raw?.bedrock_agent_invocation_id} />
          {detailsItem?.status === 'Error' && (
            <DetailRow label="Error" value={detailsItem?.errorMessage || 'No details provided.'} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const AGENT_RUNS_TABS: { key: AgentRunsTabKey; label: string; dot?: string }[] = [
  { key: 'in_progress', label: 'In progress', dot: 'statusInProgressDot' },
  { key: 'error', label: 'Errors', dot: 'statusNeedsInputDot' },
  { key: 'success', label: 'Completed', dot: 'statusVerifiedDot' },
  { key: 'all', label: 'All' },
];

const EMPTY_TEXT: Record<AgentRunsTabKey, string> = {
  in_progress: 'Nothing running right now.',
  error: 'No failed runs.',
  success: 'No finished runs yet.',
  all: 'No agent runs for this borrower yet.',
};

const COMPACT_LIMIT = 4;

export const AgentRunsCard = observer(({
  classes,
  verificationStore,
  borrowerId,
  compact = false,
}: {
  classes: Record<string, string>;
  verificationStore?: any;
  borrowerId?: number | string;
  compact?: boolean;
}) => {
  const [selectedTab, setSelectedTab] = useState<AgentRunsTabKey>(compact ? 'all' : 'in_progress');
  const [showAll, setShowAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const overview = verificationStore?.trackRecordOverview;
  const runs: Record<Exclude<AgentRunsTabKey, 'all'>, InProgressItem[]> = {
    in_progress: (overview?.in_progress_agent_invocation ?? []).map(mapOverviewItem),
    error: (overview?.error_agent_invocation ?? []).map(mapOverviewItem),
    success: (overview?.success_agent_invocation ?? []).map(mapOverviewItem),
  };
  const allRuns = [...runs.in_progress, ...runs.error, ...runs.success];
  if (allRuns.length === 0) return null;

  const tabItems = selectedTab === 'all' ? allRuns : runs[selectedTab];
  const limited = compact && !showAll;
  const visibleItems = limited ? tabItems.slice(0, COMPACT_LIMIT) : tabItems;
  const hiddenCount = tabItems.length - visibleItems.length;

  const handleRefresh = async () => {
    const id = borrowerId ?? verificationStore?.borrowerInfo?.borrowerId;
    if (!id || refreshing) return;
    setRefreshing(true);
    try {
      await verificationStore.loadFromOverview(id);
    } finally {
      setRefreshing(false);
    }
  };

  const selectTab = (key: AgentRunsTabKey) => {
    setSelectedTab(key);
    setShowAll(false);
  };

  return (
    <Box className={classes.card} style={{ marginBottom: 16 }}>
      <Box className={classes.cardHeader} style={{ alignItems: 'flex-start', marginBottom: 16 }}>
        <Box>
          <Typography className={classes.cardHeaderTitle}>
            {compact ? 'Your recent searches & uploads' : 'Agent runs'}
          </Typography>
          <Typography className={classes.overviewSubtitle} style={{ marginBottom: 0 }}>
            {compact
              ? 'Lookups and uploads started here. Results land in Properties and Entities.'
              : 'Every entity lookup, property lookup and upload the AI agent ran for this borrower.'}
          </Typography>
        </Box>
        <button
          type="button"
          className={classes.bannerButtonOutline}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshIcon style={{ fontSize: 16 }} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </Box>

      <Box className={classes.agentRunsTabs} role="tablist">
        {AGENT_RUNS_TABS.map((tab) => {
          const active = tab.key === selectedTab;
          const count = tab.key === 'all' ? allRuns.length : runs[tab.key].length;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`${classes.agentRunsTab} ${active ? classes.agentRunsTabActive : ''}`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.dot && <span className={`${classes.statusDot} ${classes[tab.dot]}`} style={{ width: 8, height: 8 }} />}
              {tab.label}
              <span className={`${classes.agentRunsTabCount} ${active ? classes.agentRunsTabCountActive : ''}`}>
                {count}
              </span>
            </button>
          );
        })}
      </Box>

      {visibleItems.length > 0 ? (
        <InProgressTable
          classes={classes}
          items={visibleItems}
          dateHeader={selectedTab === 'in_progress' ? 'Started' : 'Processed Date'}
          useStartedDate={selectedTab === 'in_progress'}
        />
      ) : (
        <Box className={classes.table}>
          <Typography className={classes.agentRunsEmpty}>{EMPTY_TEXT[selectedTab]}</Typography>
        </Box>
      )}

      {hiddenCount > 0 && (
        <span className={classes.editLink} style={{ marginTop: 12, display: 'inline-flex' }} onClick={() => setShowAll(true)}>
          Show {hiddenCount} more
        </span>
      )}
    </Box>
  );
});
